/**
 * syncService.ts — Gombita Food Auto-Sync Service
 *
 * Responsibilities:
 *  - Detect online / offline status
 *  - When offline: store API mutations in IndexedDB (via database.ts)
 *  - When back online: replay all pending mutations in FIFO order
 *  - Notify listeners (React components) of sync state changes
 *
 * ⚠️ Constraint: Does NOT modify any backend business logic.
 *    It only acts as an Outbox/Inbox pattern on top of existing API endpoints.
 */

import {
  db,
  enqueueSync,
  getPendingItems,
  markSynced,
  markFailed,
  pendingCount,
  type SyncQueueItem,
} from '../db/database';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SyncState {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSyncAt: Date | null;
  lastError: string | null;
}

type SyncListener = (state: SyncState) => void;

// ---------------------------------------------------------------------------
// Singleton SyncService
// ---------------------------------------------------------------------------

class SyncService {
  private _isOnline: boolean = navigator.onLine;
  private _isSyncing: boolean = false;
  private _pendingCount: number = 0;
  private _lastSyncAt: Date | null = null;
  private _lastError: string | null = null;
  private _listeners: Set<SyncListener> = new Set();
  private _retryTimer: ReturnType<typeof setTimeout> | null = null;

  private API_BASE_URL: string | (() => string) = '';

  constructor() {
    // Listen for browser online/offline events
    window.addEventListener('online',  () => this._handleOnline());
    window.addEventListener('offline', () => this._handleOffline());

    // Hydrate count on init
    this._refreshPendingCount();
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /** Must be called once with the resolved API base URL or a getter function */
  setApiBaseUrl(url: string | (() => string)) {
    this.API_BASE_URL = url;
  }

  get isOnline() { return this._isOnline; }
  get isSyncing() { return this._isSyncing; }
  get pendingCount() { return this._pendingCount; }

  getState(): SyncState {
    return {
      isOnline:     this._isOnline,
      isSyncing:    this._isSyncing,
      pendingCount: this._pendingCount,
      lastSyncAt:   this._lastSyncAt,
      lastError:    this._lastError,
    };
  }

  subscribe(listener: SyncListener): () => void {
    this._listeners.add(listener);
    // Immediately emit current state
    listener(this.getState());
    return () => this._listeners.delete(listener);
  }

  /**
   * Main entry point used by api.ts.
   *
   * If ONLINE  → execute fetch immediately (pass-through).
   * If OFFLINE → queue in IndexedDB and return an optimistic "queued" response.
   */
  async executeMutation(opts: {
    url: string;
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: object;
    description?: string;
    branchId?: string | null;
    optimisticResponse?: any;
  }): Promise<any> {
    const { url, method, body, description, branchId, optimisticResponse } = opts;

    if (this._isOnline) {
      // Online: execute normally
      return this._executeFetch(url, method, body, branchId);
    }

    // Offline: queue and return optimistic response
    await enqueueSync({
      url,
      method,
      body:        body ? JSON.stringify(body) : undefined,
      description: description || `${method} ${url}`,
      branchId,
    });

    await this._refreshPendingCount();
    this._notify();

    // Return an optimistic placeholder so UI doesn't break
    return optimisticResponse ?? {
      _offline: true,
      _queued:  true,
      message:  'Saved offline — will sync when back online',
    };
  }

  /**
   * Trigger a manual sync attempt (called on reconnect or manually).
   */
  async syncNow(): Promise<void> {
    if (this._isSyncing) return;
    if (!this._isOnline) return;

    const items = await getPendingItems();
    if (items.length === 0) return;

    this._isSyncing = true;
    this._lastError = null;
    this._notify();

    let successCount = 0;
    let failCount = 0;

    for (const item of items) {
      try {
        await this._replayItem(item);
        successCount++;
      } catch (err: any) {
        failCount++;
        console.warn(`[SyncService] Failed to replay item ${item.id}:`, err.message);
        this._lastError = err.message;
      }
    }

    this._isSyncing = false;
    this._lastSyncAt = new Date();
    await this._refreshPendingCount();
    this._notify();

    console.log(`[SyncService] Sync complete. Success: ${successCount}, Failed: ${failCount}`);
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private async _handleOnline() {
    console.log('[SyncService] 🌐 Back ONLINE — starting sync...');
    this._isOnline = true;
    this._notify();

    // Small delay to let network stabilise
    await new Promise(r => setTimeout(r, 1000));
    await this.syncNow();
  }

  private _handleOffline() {
    console.log('[SyncService] 📴 Gone OFFLINE — mutations will be queued.');
    this._isOnline = false;
    this._notify();
  }

  private async _replayItem(item: SyncQueueItem): Promise<void> {
    const body = item.body ? JSON.parse(item.body) : undefined;

    const baseUrl = typeof this.API_BASE_URL === 'function' ? this.API_BASE_URL() : this.API_BASE_URL;
    let url = `${baseUrl}${item.url}`;
    if (item.branchId) {
      url += `${url.includes('?') ? '&' : '?'}branchId=${item.branchId}`;
    }

    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      method: item.method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      // 409 Conflict or 4xx client errors → mark failed permanently
      if (response.status === 409 || (response.status >= 400 && response.status < 500)) {
        await db.syncQueue.update(item.id!, { status: 'failed', attempts: 99 });
        throw new Error(`Server rejected item ${item.id}: ${response.status}`);
      }
      await markFailed(item.id!);
      throw new Error(`HTTP ${response.status} for ${item.url}`);
    }

    await markSynced(item.id!);
  }

  private async _refreshPendingCount(): Promise<void> {
    this._pendingCount = await pendingCount();
  }

  private _notify(): void {
    const state = this.getState();
    this._listeners.forEach(fn => fn(state));
  }
}

// ---------------------------------------------------------------------------
// Export singleton
// ---------------------------------------------------------------------------

export const syncService = new SyncService();
